document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const initNightSafeReveal = (scope = document) => {
  if (!('IntersectionObserver' in window)) return;

  document.documentElement.classList.add('reveal-ready');
  const elements = scope.matches && scope.matches('[data-reveal]')
    ? [scope, ...scope.querySelectorAll('[data-reveal]')]
    : [...scope.querySelectorAll('[data-reveal]')];

  const observer = new IntersectionObserver((entries, currentObserver) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      currentObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  elements.forEach((element) => observer.observe(element));
};

const initBeforeAfter = (scope = document) => {
  const sliders = scope.matches && scope.matches('[data-before-after-range]')
    ? [scope]
    : [...scope.querySelectorAll('[data-before-after-range]')];

  sliders.forEach((slider) => {
    const updatePosition = () => {
      const comparison = slider.closest('[data-before-after]');
      if (comparison) comparison.style.setProperty('--comparison-position', `${slider.value}%`);
    };

    slider.addEventListener('input', updatePosition);
    updatePosition();
  });
};

const respectReducedMotion = (scope = document) => {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const videos = scope.matches && scope.matches('.ns-autoplay-video')
    ? [scope]
    : [...scope.querySelectorAll('.ns-autoplay-video')];
  videos.forEach((video) => video.pause());
};

const initProductVariants = (scope = document) => {
  const products = scope.matches && scope.matches('[data-product-root]')
    ? [scope]
    : [...scope.querySelectorAll('[data-product-root]')];

  products.forEach((productRoot) => {
    if (productRoot.dataset.variantPickerInitialized === 'true') return;
    productRoot.dataset.variantPickerInitialized = 'true';

    const variantsElement = productRoot.querySelector('[data-product-variants]');
    const variantIdInput = productRoot.querySelector('[data-product-variant-id]');
    const optionInputs = [...productRoot.querySelectorAll('[data-product-option]')];
    const price = productRoot.querySelector('[data-product-price]');
    const compareAt = productRoot.querySelector('[data-product-compare-price]');
    const availability = productRoot.querySelector('[data-product-availability]');
    const addButton = productRoot.querySelector('[data-add-to-cart]');
    const gallery = productRoot.querySelector('.product-gallery');

    if (!variantsElement || !variantIdInput || !addButton) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch (error) {
      return;
    }

    const getSelectedOptions = () => optionInputs
      .filter((input) => input.checked)
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);

    const findSelectedVariant = () => {
      if (!optionInputs.length) {
        return variants.find((variant) => String(variant.id) === String(variantIdInput.value));
      }
      const selectedOptions = getSelectedOptions();
      return variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index]));
    };

    const updateOptionStates = () => {
      optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionPosition) - 1;
        const matchingVariants = variants.filter((variant) => variant.options[optionIndex] === input.value);
        const canChoose = matchingVariants.some((variant) => variant.available);
        const uniqueVariant = matchingVariants.length === 1 ? matchingVariants[0] : null;
        const label = input.closest('.product-option-value');
        const optionPrice = label && label.querySelector('[data-option-price]');
        const optionStatus = label && label.querySelector('[data-option-status]');

        input.disabled = !canChoose;
        if (label) {
          label.classList.toggle('is-selected', input.checked);
          label.classList.toggle('is-disabled', !canChoose);
          label.setAttribute('aria-disabled', String(!canChoose));
        }
        if (optionPrice) optionPrice.textContent = uniqueVariant ? uniqueVariant.priceFormatted : '';
        if (optionStatus) optionStatus.hidden = canChoose;
      });
    };

    const updateMedia = (variant) => {
      if (!gallery || !variant || !variant.featuredMediaId) return;
      const targetMedia = gallery.querySelector(`[data-product-media-id="${variant.featuredMediaId}"]`);
      if (!targetMedia) return;
      const currentMedia = gallery.querySelector('.product-gallery__item--main');
      if (currentMedia && currentMedia !== targetMedia) {
        currentMedia.classList.remove('product-gallery__item--main');
        currentMedia.removeAttribute('aria-current');
      }
      targetMedia.classList.add('product-gallery__item--main');
      targetMedia.setAttribute('aria-current', 'true');
      if (typeof gallery.scrollTo === 'function') gallery.scrollTo({ left: 0, behavior: 'smooth' });
    };

    const updateVariant = (variant, updateUrl = false) => {
      const isAvailable = Boolean(variant && variant.available);
      variantIdInput.value = variant ? variant.id : '';

      if (price) price.textContent = variant ? variant.priceFormatted : '—';
      if (compareAt) {
        const showCompareAt = Boolean(variant && variant.compareAt > variant.price);
        compareAt.hidden = !showCompareAt;
        compareAt.textContent = showCompareAt ? variant.compareAtFormatted : '';
      }
      if (availability) {
        availability.textContent = isAvailable ? 'Disponible' : 'Épuisé';
        availability.classList.toggle('is-sold-out', !isAvailable);
      }
      addButton.disabled = !isAvailable;
      addButton.textContent = isAvailable ? addButton.dataset.availableText : addButton.dataset.soldOutText;

      updateMedia(variant);

      if (variant && updateUrl) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (error) {
          // Le formulaire reste synchronisé même si l’URL ne peut pas être modifiée.
        }
      }

      productRoot.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updateOptionStates();
        updateVariant(findSelectedVariant(), true);
      });
    });

    updateOptionStates();
    updateVariant(findSelectedVariant());
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initNightSafeReveal();
  initBeforeAfter();
  respectReducedMotion();
  initProductVariants();

  const menuButton = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');

  if (menuButton && mobileMenu) {
    menuButton.addEventListener('click', () => {
      const isOpen = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!isOpen));
      mobileMenu.hidden = isOpen;
      document.body.classList.toggle('menu-open', !isOpen);
    });
  }

  document.querySelectorAll('[data-quantity-control]').forEach((control) => {
    const input = control.querySelector('input[type="number"]');
    if (!input) return;

    control.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(input.step || 1);
        const minimum = Number(input.min || 1);
        const nextValue = button.dataset.action === 'increase'
          ? Number(input.value) + step
          : Math.max(minimum, Number(input.value) - step);
        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  document.querySelectorAll('[data-copy-link]').forEach((button) => {
    button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const oldText = button.textContent;
        button.textContent = 'Lien copié !';
        setTimeout(() => { button.textContent = oldText; }, 1800);
      } catch (error) {
        window.prompt('Copiez ce lien :', window.location.href);
      }
    });
  });
});

document.addEventListener('shopify:section:load', (event) => {
  initNightSafeReveal(event.target);
  initBeforeAfter(event.target);
  respectReducedMotion(event.target);
  initProductVariants(event.target);
});
